<template>
  <transition-root appear as="template" :show="show">
    <h-dialog
      class="relative z-30"
      tabindex="-1"
      v-bind="$attrs"
      @close="close"
    >
      <transition-child
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25"/>
      </transition-child>

      <focus-first
        :class="[
          'fixed inset-0',
          scrollable && 'overflow-y-auto',
          scrollable && size === 'screen' && 'overscroll-none',
        ]"
      >
        <div
          :class="[
            'flex min-h-full',
            size !== 'screen' && 'items-center justify-center p-4',
            size === 'screen' && 'items-stretch',
          ]"
        >
          <transition-child
            as="template"
            enter="duration-300 ease-out"
            :enter-from="clsx([
              slide || 'opacity-0 scale-95',
              slide === 'up' && 'translate-y-full',
              slide === 'down' && '-translate-y-full',
              slide === 'left' && 'translate-x-full',
              slide === 'right' && '-translate-x-full',
            ])"
            :enter-to="clsx([
              slide || 'opacity-100 scale-100',
              slide && 'translate-0',
            ])"
            leave="duration-200 ease-in"
            :leave-from="clsx([
              slide || 'opacity-100 scale-100',
              slide && 'translate-0',
            ])"
            :leave-to="clsx([
              slide || 'opacity-0 scale-95',
              slide === 'up' && 'translate-y-full',
              slide === 'down' && '-translate-y-full',
              slide === 'left' && 'translate-x-full',
              slide === 'right' && '-translate-x-full',
            ])"
          >
            <dialog-panel
              :class="[
                'bg-white relative flex flex-col w-full gap-5 p-4 sm:p-6',
                'dark:bg-gray-900 dark:text-white',
                size === 'lg' && 'max-w-lg',
                size === 'xl' && 'max-w-xl',
                size === '2xl' && 'max-w-2xl',
                size === '3xl' && 'max-w-3xl',
                size === '4xl' && 'max-w-4xl',
                size !== 'screen' && 'rounded-lg shadow-xl',
              ]"
            >
              <dialog-title as="h2" class="font-sans font-semibold leading-6 text-inherit text-lg ring-0 dark:ring-0" tabindex="0">
                <slot name="title"/>

                {{ title }}
              </dialog-title>

              <slot/>

              <slot name="body"/>

              <div v-if="$slots.footer" class="flex flex-row-reverse gap-2">
                <slot name="footer"/>
              </div>

              <l-button
                aria-label="Close"
                :class="[
                  size === 'screen' ? 'fixed' : 'absolute',
                  'top-2 right-2 flex items-center justify-center size-8 p-2 sm:top-4 sm:right-4',
                  'rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300',
                ]"
                variant="link"
                @click="close"
              >
                <icon name="close"/>
              </l-button>
            </dialog-panel>
          </transition-child>
        </div>
      </focus-first>
    </h-dialog>
  </transition-root>
</template>

<script setup>
import FocusFirst from "@/components/FocusFirst.vue"
import Icon from "@/components/Icon.vue"
import LButton from "@/components/buttons/LButton.vue"
import {
  DialogPanel,
  DialogTitle,
  Dialog as HDialog,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue"
import { clsx } from "clsx"

defineProps({
  title: String,
  scrollable: Boolean,
  show: Boolean,
  size: { type: String, default: "lg" },
  slide: String,
})

const emit = defineEmits(["close"])

const close = () => emit("close")
</script>
